"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.extractMetricsFromChimeMetricReports = extractMetricsFromChimeMetricReports;
/**
 * This function extracts network health data relevant metrics in a flat form
 * from Chime metric reports received in MetricsDidReceive callback
 * currentAttendeeId is used here because videoMetricReport contains video
 * metrics for all meeting participants, and we are interested here only for the current participant (i.e. self)
 */
function extractMetricsFromChimeMetricReports(clientMetricReport, currentAttendeeId) {
  var metricReport = clientMetricReport.getObservableMetrics();
  var metrics = {};
  metrics['availableSendBandwidthKbps'] = getMetricValue(metricReport, 'availableOutgoingBitrate') / 1000;
  metrics['availableReceiveBandwidthKbps'] = getMetricValue(metricReport, 'availableIncomingBitrate') / 1000;
  metrics['upstreamAudioPacketsSent'] = getMetricValue(metricReport, 'audioPacketsSent');
  metrics['upstreamAudioPacketLoss'] = getMetricValue(metricReport, 'audioPacketLossPercent');
  metrics['upstreamAudioJitter'] = getMetricValue(metricReport, 'audioUpstreamJitterMs');
  metrics['upstreamAudioRoundTripTimeMs'] = getMetricValue(metricReport, 'currentRoundTripTimeMs');
  var videoData = getCurrentAttendeeVideoMetrics(clientMetricReport, currentAttendeeId);
  metrics['upstreamVideoPacketsSent'] = getMetricValue(videoData, 'videoUpstreamPacketsSent');
  metrics['upstreamVideoPacketLoss'] = getMetricValue(videoData, 'videoUpstreamPacketLossPercent');
  metrics['upstreamVideoJitter'] = getMetricValue(videoData, 'videoUpstreamJitterMs');
  metrics['upstreamVideoRoundTripTimeMs'] = getMetricValue(videoData, 'videoUpstreamRoundTripTimeMs');
  metrics['upstreamVideoFPS'] = getMetricValue(videoData, 'videoUpstreamFramesInputPerSecond');
  metrics['upstreamVideoFrameWidth'] = getMetricValue(videoData, 'videoUpstreamFrameWidth');
  metrics['upstreamVideoFrameHeight'] = getMetricValue(videoData, 'videoUpstreamFrameHeight');
  return metrics;
}

/**
 * Get video metrics for attendee from videoMetricReport.
 * videoMetricReport has the following format:
 * {
 *   'attendee-1': {
 *     'stream-11': { ...video metrics data },
 *     'stream-12': { ...video metrics data }, ... so on
 *   },
 *   'attendee-2': {
 *     'stream-21': { ...video metrics data },
 *     'stream-22': { ...video metrics data }, ... so on
 *   },
 *   ... so on
 * }
 *
 * For an attendee, there can be multiple streams and each stream has its own video metrics
 * Usually, there is only one stream for each attendee but there are cases when there can be multiple streams like:
 *  - when simulcast is enabled, it creates multiple streams - usually 2 or 3
 *  - Sometimes there is an additional stream which doesn't contain any data (null or zero values) - bogus streams
 *  - In some cases, when client reconnects to Chime, it creates a new stream but the old stream is not properly cleaned up
 *    This old stream remains there in videoMetricReport and contains valid-looking data but these are just last known values
 *    before reconnection
 *
 * Since we don't use simulcast (in favor of SVC), we'll ignore that case
 * So, we'll filter out bogus streams and use the current active one which contains valid data
 *
 * ===== SPECIAL BROWSER HANDLING ====
 * Ideally, when video is turned off, there should be no data (or null/zero-value data) in videoMetricReport for the attendee
 * So, this function will return a blank object ({})
 * but in some browsers (like firefox or possibly some edge cases in Chrome), we keep receiving data in videoMetricReport even when video is off
 * this can lead to unintended behaviour
 * To handle that, we additionally need to check outbound-rtp metrics of kind video in raw WebRTC stats
 */
function getCurrentAttendeeVideoMetrics(clientMetricReport, currentAttendeeId) {
  /**
   * Check first if raw WebRTC stats has valid video data
   */
  var rtcStatsReport = clientMetricReport.getRTCStatsReport();
  var activeOutboundRTPVideoStream = null;
  rtcStatsReport.forEach(function (report) {
    if (report.type === 'outbound-rtp' && report.kind === 'video' && report.active === true && report.packetsSent > 0) {
      activeOutboundRTPVideoStream = report.ssrc;
    }
  });

  /**
   * Either outbound-rtp of kind video was not present in WebRTC stats
   * or it has 0 packets sent (both indicating video off)
   */
  if (activeOutboundRTPVideoStream == null) {
    return {};
  }

  /**
   * Now check for valid values in video metric report of Chime
   */
  var videoMetricReport = clientMetricReport.getObservableVideoMetrics();
  if (!videoMetricReport.hasOwnProperty(currentAttendeeId)) {
    return {};
  }
  var currentAttendeeVideoMetricReport = videoMetricReport[currentAttendeeId];
  /**
   * Video metric report must have an entry for outbound RTP Video Stream
   * NOTE: Video metric report can have stale entries e.g. in cases where client reconnects
   * to Chime after network issue, it sometimes creates a new stream
   * But the old stream is not cleaned up and remains frozen at last values
   * So it's important to filter out and use the current active stream
   */
  if (!currentAttendeeVideoMetricReport.hasOwnProperty(activeOutboundRTPVideoStream)) {
    return {};
  }
  return currentAttendeeVideoMetricReport[activeOutboundRTPVideoStream];
}
function getMetricValue(metricsObject, key) {
  var defaultValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
  return metricsObject.hasOwnProperty(key) ? metricsObject[key] : defaultValue;
}